import { Koi } from '@ksyos/koi';
import { DeviceStatusReferenceType } from '@common/enums/deviceStatusReferenceType';
import { csProcessIdRegex } from '@common/utils/regularExpressions';
import type { ISchemaContainer } from './schemaContainer';
import { DeviceStatus } from '../enums/deviceStatus';
import { replaceKoiError } from './replaceKoiError';

export interface IDeviceStatusEditValidated {
    deviceStatus: {
        reference: string | null;
        referenceType: DeviceStatusReferenceType | null;
        status: DeviceStatus;
    };
    organizationIds: string[];
}

const issuedDeviceStatusSchema = Koi.object({
    reference: Koi.allow(null),
    referenceType: Koi.allow(null),
    status: Koi.valid(DeviceStatus.issued),
});

const withPatientDeviceStatusSchema = Koi.object({
    reference: Koi.string()
        .regex(csProcessIdRegex)
        .error((errors) => replaceKoiError(errors, 'string.pattern.base', 'any_invalid')),
    referenceType: Koi.valid(DeviceStatusReferenceType.processId),
    status: Koi.valid(DeviceStatus.withPatient),
});

const internalUseDeviceStatusSchema = Koi.object({
    reference: Koi.string().uuid(),
    referenceType: Koi.valid(DeviceStatusReferenceType.practitionerId),
    status: Koi.valid(DeviceStatus.internalUse),
});

const deviceStatusEmptyReferenceSchema = Koi.object({
    reference: Koi.allow(null),
    referenceType: Koi.allow(null),
    status: Koi.koi()
        .enum(DeviceStatus)
        .disallow(DeviceStatus.issued, DeviceStatus.withPatient, DeviceStatus.internalUse),
});

export const deviceStatusSchema = Koi.alternatives().conditional('deviceStatus.status', [
    { is: DeviceStatus.withPatient, then: withPatientDeviceStatusSchema },
    { is: DeviceStatus.internalUse, then: internalUseDeviceStatusSchema },
    { is: DeviceStatus.issued, otherwise: deviceStatusEmptyReferenceSchema, then: issuedDeviceStatusSchema },
]);

export const deviceStatusEditSchema: ISchemaContainer<IDeviceStatusEditValidated> = {
    schema: Koi.object({
        deviceStatus: deviceStatusSchema,
        organizationIds: Koi.when('deviceStatus.status', {
            is: DeviceStatus.issued,
            otherwise: Koi.array().length(0),
            then: Koi.array().items(Koi.string().uuid()).min(1),
        }),
    }).options({ convert: false, presence: 'required' }),
};
